import React, { useState, useRef, useEffect } from 'react';
import '../FileUpload.css';
import ClipLoader from 'react-spinners/ClipLoader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileAlt,
  faTimes,
  faCloudUploadAlt,
  faFolderOpen,
  faBriefcase,
  faCogs,
  faUsers,
  faCheckCircle,
  faTrashAlt,
  faSlidersH,
  faLayerGroup,
  faCheck,
  faInfoCircle,
  faSparkles,
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { backend_url } from '../config';
import api from '../axiosInstance';
import {
  experienceYearsRanges,
  allJobRolesData,
  sapModulesData,
} from './Constants';
import { useAnalysis } from '../context/AnalysisContext';

// Helper to format file sizes cleanly (e.g., 1.2 MB)
const formatBytes = (bytes, decimals = 1) => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

// Helper to determine badge color based on file extension
const getFileExt = (filename) => {
  if (!filename) return 'TXT';
  const ext = filename.split('.').pop().toUpperCase();
  return ext;
};

// Function to generate comprehensive job description based on any job role
const generateJobDescription = (
  jobRoleName,
  skills = [],
  experienceLevel = ''
) => {
  if (!jobRoleName) return '';

  const isSapRole = Object.prototype.hasOwnProperty.call(
    sapModulesData,
    jobRoleName
  );

  let jobTitle = `${jobRoleName}`;
  let jobDescription = `Job Title: ${jobTitle}

Job Summary:
We are seeking an experienced ${jobRoleName} to join our dynamic team. The ideal candidate will have hands-on experience in relevant technologies and practices.

Key Responsibilities:
• Design, develop, and maintain solutions relevant to ${jobRoleName}
• Collaborate with cross-functional teams to define, design, and ship new features
• Ensure the performance, quality, and responsiveness of applications/systems
• Identify and correct bottlenecks and fix bugs
• Help maintain code quality, organization, and automation
• Participate in code reviews and contribute to team knowledge sharing
• Stay up-to-date with emerging technologies and industry trends
`;

  if (experienceLevel) {
    jobDescription += `
Required Experience:
• ${experienceLevel} of hands-on experience in ${jobRoleName}
`;
  } else {
    jobDescription += `
Required Experience:
• Minimum 3+ years of hands-on experience in ${jobRoleName}
`;
  }

  if (skills.length > 0) {
    jobDescription += `
Required Skills and Technologies:
`;
    skills.forEach((skill) => {
      jobDescription += `• ${skill}\n`;
    });
  } else {
    const roleSkills = allJobRolesData[jobRoleName] || [];
    if (roleSkills.length > 0) {
      jobDescription += `
Required Skills and Technologies:
`;
      roleSkills.slice(0, 10).forEach((skill) => {
        jobDescription += `• ${skill}\n`;
      });
      if (roleSkills.length > 10) {
        jobDescription += `• And other relevant skills for a ${jobRoleName} role\n`;
      }
    }
  }

  jobDescription += `

Additional Requirements:
• Bachelor's degree in Computer Science, Information Technology, or a related field
• Strong analytical and problem-solving skills
• Excellent communication and interpersonal skills
• Experience working in collaborative environment
`;

  if (isSapRole) {
    jobDescription += `
• SAP certification in ${jobRoleName} (highly preferred)
• Experience with SAP implementation methodologies
`;
  }

  return jobDescription;
};

function FileUpload({ setAnalysisResults }) {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [jobDescriptionFile, setJobDescriptionFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [selectedJobRole, setSelectedJobRole] = useState('');
  const [availableSkills, setAvailableSkills] = useState([]);
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [selectedExperience, setSelectedExperience] = useState('');

  const [generatedTextPrefix, setGeneratedTextPrefix] = useState('');
  const [additionalUserText, setAdditionalUserText] = useState('');
  const [isDragOverResumes, setIsDragOverResumes] = useState(false);
  const [isDragOverJd, setIsDragOverJd] = useState(false);

  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const jobDescriptionFileInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (selectedJobRole && allJobRolesData[selectedJobRole]) {
      setAvailableSkills(allJobRolesData[selectedJobRole]);
      setSelectedSkills([]);
    } else {
      setAvailableSkills([]);
      setSelectedSkills([]);
    }
  }, [selectedJobRole]);

  useEffect(() => {
    if (selectedJobRole) {
      const fullJobDescription = generateJobDescription(
        selectedJobRole,
        selectedSkills,
        selectedExperience
      );
      setGeneratedTextPrefix(fullJobDescription);
      return;
    }

    let prefix = '';
    if (selectedSkills.length > 0) {
      prefix += `Required Skills: ${selectedSkills.join(', ')}\n`;
    }
    if (selectedExperience) {
      prefix += `Required Experience: ${selectedExperience}\n`;
    }
    if (prefix) {
      prefix += '\n--- Additional Job Details (edit or add below) ---\n';
    }
    setGeneratedTextPrefix(prefix);
  }, [selectedJobRole, selectedSkills, selectedExperience]);

  const processFiles = (files) => {
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
    ];
    const validFiles = files.filter((file) => allowedTypes.includes(file.type));
    const invalidFiles = files.filter(
      (file) => !allowedTypes.includes(file.type)
    );

    let errorMsg = '';
    if (invalidFiles.length > 0) {
      errorMsg = `Unsupported resume file type(s): ${invalidFiles
        .map((f) => f.name)
        .join(', ')}. Allowed formats: PDF, DOC, DOCX, TXT`;
    }
    setErrorMessage(errorMsg);

    if (errorMsg === '') {
      const newUniqueFiles = validFiles.filter(
        (vf) =>
          !selectedFiles.some(
            (sf) => sf.name === vf.name && sf.size === vf.size
          )
      );
      setSelectedFiles((prevFiles) => [...prevFiles, ...newUniqueFiles]);
    }
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    processFiles(files);
    event.target.value = null;
  };

  const handleFolderChange = (event) => {
    const files = Array.from(event.target.files);
    processFiles(files);
    event.target.value = null;
  };

  const handleJobDescriptionFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const allowedJdTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
    ];
    if (!allowedJdTypes.includes(file.type)) {
      setErrorMessage(
        `Unsupported job description file type: ${file.name}. Allowed: PDF, DOC, DOCX, TXT`
      );
      if (jobDescriptionFileInputRef.current) {
        jobDescriptionFileInputRef.current.value = '';
      }
      setJobDescriptionFile(null);
      return;
    }
    setErrorMessage('');
    setJobDescriptionFile(file);
    setSelectedJobRole('');
    setSelectedSkills([]);
    setSelectedExperience('');
    setAdditionalUserText('');
    event.target.value = null;
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const handleClearAllFiles = () => {
    setSelectedFiles([]);
  };

  const handleRemoveJobDescriptionFile = () => {
    setJobDescriptionFile(null);
    if (jobDescriptionFileInputRef.current) {
      jobDescriptionFileInputRef.current.value = '';
    }
  };

  const handleJobRoleChange = (event) => {
    setSelectedJobRole(event.target.value);
    setAdditionalUserText('');
    if (event.target.value) {
      setJobDescriptionFile(null);
      if (jobDescriptionFileInputRef.current)
        jobDescriptionFileInputRef.current.value = '';
    }
  };

  const handleSkillChange = (event) => {
    const { value, checked } = event.target;
    const newSkills = checked
      ? [...selectedSkills, value]
      : selectedSkills.filter((skill) => skill !== value);
    setSelectedSkills(newSkills);
    if (newSkills.length > 0 || checked) {
      setJobDescriptionFile(null);
      if (jobDescriptionFileInputRef.current)
        jobDescriptionFileInputRef.current.value = '';
    }
  };

  const handleExperienceChange = (event) => {
    setSelectedExperience(event.target.value);
    if (event.target.value) {
      setJobDescriptionFile(null);
      if (jobDescriptionFileInputRef.current)
        jobDescriptionFileInputRef.current.value = '';
    }
  };

  const handleCombinedJobDescriptionChange = (event) => {
    const fullText = event.target.value;
    if (generatedTextPrefix && fullText.startsWith(generatedTextPrefix)) {
      setAdditionalUserText(fullText.substring(generatedTextPrefix.length));
    } else {
      setAdditionalUserText(fullText);
    }
    if (fullText.trim() !== '') {
      setJobDescriptionFile(null);
      if (jobDescriptionFileInputRef.current)
        jobDescriptionFileInputRef.current.value = '';
    }
  };

  const { startAnalysis, isAnalyzing } = useAnalysis();

  const handleFileUpload = async () => {
    const completeJobDescriptionText = (
      generatedTextPrefix + additionalUserText
    ).trim();

    if (!selectedFiles.length) {
      setErrorMessage('Please upload candidate resumes to proceed.');
      return;
    }

    const isJdProvided =
      selectedJobRole ||
      jobDescriptionFile ||
      completeJobDescriptionText.length > 50;

    if (!isJdProvided) {
      setErrorMessage(
        'Please provide a job description by selecting a Job Role, entering requirements, or uploading a JD file.'
      );
      return;
    }

    setErrorMessage('');
    const formData = new FormData();
    selectedFiles.forEach((file) => {
      formData.append('resumes', file, file.name);
    });

    if (jobDescriptionFile) {
      formData.append(
        'jobDescriptionFile',
        jobDescriptionFile,
        jobDescriptionFile.name
      );
    } else if (completeJobDescriptionText) {
      formData.append('jobDescriptionText', completeJobDescriptionText);
    }

    startAnalysis(formData, selectedJobRole || 'Job Role', selectedFiles.length);
  };

  const jobDescriptionForTextarea = generatedTextPrefix + additionalUserText;

  const isAnalyzeButtonDisabled =
    isLoading ||
    !selectedFiles.length ||
    (!selectedJobRole &&
      !jobDescriptionFile &&
      jobDescriptionForTextarea.trim().length < 50);

  return (
    <div className='coreops-app-container'>
      {/* Enterprise Graphic Header */}
      <header className='coreops-main-header'>
        <div className='header-badge'>
          <span className='pulse-dot'></span> COREHire Engine 3.0
        </div>
        <h1>AI Talent & Resume Analyzer</h1>
        <p className='coreops-subtitle'>
          High-precision multi-candidate parsing & automated qualification engine
        </p>

        {/* Feature Highlights Banner */}
        <div className='hero-features-bar'>
          <div className='feature-pill'>
            <FontAwesomeIcon icon={faCloudUploadAlt} className='pill-icon' />
            <span>Batch Resume Parsing</span>
          </div>
          <div className='feature-pill'>
            <FontAwesomeIcon icon={faLayerGroup} className='pill-icon' />
            <span>Auto-Job Spec Generator</span>
          </div>
          <div className='feature-pill'>
            <FontAwesomeIcon icon={faCheckCircle} className='pill-icon' />
            <span>Smart Candidate Scoring</span>
          </div>
        </div>
      </header>

      {/* Main Workspace */}
      <main className='coreops-main-content'>
        <div className='coreops-file-upload-layout compact-layout'>
          
          {/* STEP 1: RESUME UPLOAD SECTION */}
          <section className='coreops-upload-section resume-section'>
            <div className='section-header-wrapper'>
              <span className='step-number-badge'>1</span>
              <div>
                <h2 className='coreops-section-title'>
                  <FontAwesomeIcon icon={faUsers} /> Upload Candidate Resumes
                </h2>
                <p className='section-subtitle'>Drop single files, multiple PDFs, or full candidate folders</p>
              </div>
            </div>

            <div
              className={`coreops-drag-drop-area ${isDragOverResumes ? 'drag-over' : ''}`}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragOverResumes(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                setIsDragOverResumes(false);
              }}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragOverResumes(false);
                const files = Array.from(e.dataTransfer.files);
                processFiles(files);
              }}
            >
              <div className='upload-icon-wrapper'>
                <FontAwesomeIcon
                  icon={faCloudUploadAlt}
                  className='coreops-upload-icon'
                />
              </div>
              <p className='drag-drop-text'>Drag & Drop Resumes Here</p>
              <p className='drag-drop-subtext'>Supported Formats: PDF, DOC, DOCX, TXT</p>
              
              <div className='format-pills-row'>
                <span className='format-pill pdf'>PDF</span>
                <span className='format-pill docx'>DOCX</span>
                <span className='format-pill doc'>DOC</span>
                <span className='format-pill txt'>TXT</span>
              </div>

              <div className='drag-drop-or'>
                <span>OR BROWSE</span>
              </div>

              <div className='coreops-button-group'>
                <button
                  type='button'
                  className='coreops-button coreops-button-secondary'
                  onClick={() => fileInputRef.current && fileInputRef.current.click()}
                >
                  <FontAwesomeIcon icon={faFileAlt} /> Select Resumes
                </button>
                <button
                  type='button'
                  className='coreops-button coreops-button-secondary'
                  onClick={() => folderInputRef.current && folderInputRef.current.click()}
                >
                  <FontAwesomeIcon icon={faFolderOpen} /> Upload Folder
                </button>
              </div>

              <input
                type='file'
                multiple
                accept='.pdf,.doc,.docx,.txt'
                onChange={handleFileChange}
                style={{ display: 'none' }}
                ref={fileInputRef}
              />
              <input
                type='file'
                webkitdirectory='true'
                directory=''
                multiple
                onChange={handleFolderChange}
                style={{ display: 'none' }}
                ref={folderInputRef}
              />
            </div>

            {/* Selected Resumes Batch View */}
            {selectedFiles.length > 0 ? (
              <div className='coreops-selected-files-container'>
                <div className='files-summary-header'>
                  <h4 className='coreops-selected-files-title'>
                    Uploaded Resumes ({selectedFiles.length})
                  </h4>
                  <button
                    type='button'
                    className='clear-all-button'
                    onClick={handleClearAllFiles}
                    title='Clear all selected files'
                  >
                    <FontAwesomeIcon icon={faTrashAlt} /> Clear All
                  </button>
                </div>

                <div className='coreops-selected-files-list scrollable'>
                  {selectedFiles.map((file, index) => {
                    const ext = getFileExt(file.name);
                    return (
                      <div
                        className='coreops-file-item'
                        key={index + '-' + file.name + '-' + file.size}
                      >
                        <div className={`file-ext-badge ${ext.toLowerCase()}`}>
                          {ext}
                        </div>
                        <div className='file-details-col'>
                          <span className='coreops-file-name' title={file.name}>
                            {file.name}
                          </span>
                          <span className='file-size-subtext'>
                            {formatBytes(file.size)}
                          </span>
                        </div>
                        <button
                          type='button'
                          className='coreops-remove-file-button'
                          onClick={() => handleRemoveFile(index)}
                          title='Remove file'
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className='empty-files-placeholder'>
                <FontAwesomeIcon icon={faInfoCircle} className='empty-icon' />
                <span>No resumes attached yet. Select files or drag & drop to get started.</span>
              </div>
            )}
          </section>

          {/* STEP 2: JOB DESCRIPTION SECTION */}
          <section className='coreops-upload-section job-description-section'>
            <div className='section-header-wrapper'>
              <span className='step-number-badge step-2'>2</span>
              <div>
                <h2 className='coreops-section-title'>
                  <FontAwesomeIcon icon={faBriefcase} /> Provide Job Specification
                </h2>
                <p className='section-subtitle'>Auto-generate requirements by role or upload a JD document</p>
              </div>
            </div>

            <div className='coreops-structured-jd-helpers'>
              <div className='helper-title-row'>
                <FontAwesomeIcon icon={faSlidersH} className='helper-icon' />
                <h4 className='coreops-subsection-title'>
                  Role-Based Smart Generator
                </h4>
              </div>

              <div className='coreops-jd-input-group'>
                <label htmlFor='job-role-select'>Target Job Role:</label>
                <div className='select-wrapper'>
                  <select
                    id='job-role-select'
                    value={selectedJobRole}
                    onChange={handleJobRoleChange}
                    className='coreops-select'
                  >
                    <option value=''>-- Select Target Role --</option>
                    {Object.keys(allJobRolesData).map((roleName) => (
                      <option key={roleName} value={roleName}>
                        {roleName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {!selectedJobRole && availableSkills.length > 0 && (
                <div className='coreops-jd-input-group'>
                  <label>Required Skill Tags:</label>
                  <div className='coreops-skills-checkbox-group scrollable'>
                    {availableSkills.map((skill) => (
                      <div key={skill} className='coreops-skill-checkbox-item'>
                        <input
                          type='checkbox'
                          id={`skill-${skill.replace(/\s+/g, '-')}`}
                          value={skill}
                          checked={selectedSkills.includes(skill)}
                          onChange={handleSkillChange}
                        />
                        <label htmlFor={`skill-${skill.replace(/\s+/g, '-')}`}>
                          {skill}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className='coreops-jd-input-group'>
                <label htmlFor='experience-select'>Experience Required:</label>
                <div className='select-wrapper'>
                  <select
                    id='experience-select'
                    value={selectedExperience}
                    onChange={handleExperienceChange}
                    className='coreops-select'
                  >
                    <option value=''>-- Select Experience Level --</option>
                    {experienceYearsRanges.map((range) => (
                      <option key={range} value={range}>
                        {range}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Editable Job Description Textarea */}
            <div className='coreops-jd-main-textbox-section'>
              <div className='textbox-header-row'>
                <h4 className='coreops-subsection-title'>
                  {selectedJobRole
                    ? '✨ Auto-Generated Specification (Editable)'
                    : 'Job Description Text'}
                </h4>
                {selectedJobRole && (
                  <span className='auto-gen-tag'>
                    <FontAwesomeIcon icon={faCheck} /> Auto-Filled
                  </span>
                )}
              </div>
              <textarea
                rows='12'
                value={jobDescriptionForTextarea}
                onChange={handleCombinedJobDescriptionChange}
                placeholder={
                  selectedJobRole
                    ? 'Auto-generated specification based on selected role. Edit or append custom requirements here...'
                    : 'Select a target job role above or paste/type custom job description details...'
                }
                className='coreops-textarea'
              />
            </div>

            <div className='coreops-jd-separator'>
              <span>OR ATTACH JD FILE</span>
            </div>

            {/* Alternative JD File Upload */}
            <div className='coreops-jd-file-upload-area'>
              <div
                className={`coreops-drag-drop-area coreops-drag-drop-area-small ${isDragOverJd ? 'drag-over' : ''}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragOverJd(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  setIsDragOverJd(false);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDragOverJd(false);
                  if (e.dataTransfer.files.length > 0) {
                    const file = e.dataTransfer.files[0];
                    const allowedJdTypes = [
                      'application/pdf',
                      'application/msword',
                      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                      'text/plain',
                    ];
                    if (allowedJdTypes.includes(file.type)) {
                      const mockEvent = { target: { files: [file] } };
                      handleJobDescriptionFileChange(mockEvent);
                    } else {
                      setErrorMessage(
                        `Unsupported JD file type: ${file.name}. Allowed: PDF, DOC, DOCX, TXT`
                      );
                    }
                  }
                }}
              >
                <FontAwesomeIcon
                  icon={faCloudUploadAlt}
                  className='coreops-upload-icon small'
                />
                <p className='drag-drop-text small'>Drop Job Description File Here</p>
                <p className='drag-drop-subtext small'>PDF, DOC, DOCX, TXT</p>
                <button
                  type='button'
                  className='coreops-button coreops-button-secondary coreops-button-small'
                  onClick={() =>
                    jobDescriptionFileInputRef.current &&
                    jobDescriptionFileInputRef.current.click()
                  }
                >
                  Browse JD File
                </button>
                <input
                  type='file'
                  accept='.pdf,.doc,.docx,.txt'
                  onChange={handleJobDescriptionFileChange}
                  style={{ display: 'none' }}
                  ref={jobDescriptionFileInputRef}
                />
              </div>

              {jobDescriptionFile && (
                <div className='coreops-selected-files-list single-file'>
                  <h4 className='coreops-selected-files-title'>Attached Job Description:</h4>
                  <div className='coreops-file-item jd-active-item'>
                    <div className={`file-ext-badge ${getFileExt(jobDescriptionFile.name).toLowerCase()}`}>
                      {getFileExt(jobDescriptionFile.name)}
                    </div>
                    <div className='file-details-col'>
                      <span className='coreops-file-name' title={jobDescriptionFile.name}>
                        {jobDescriptionFile.name}
                      </span>
                      <span className='file-size-subtext'>
                        {formatBytes(jobDescriptionFile.size)}
                      </span>
                    </div>
                    <button
                      type='button'
                      className='coreops-remove-file-button'
                      onClick={handleRemoveJobDescriptionFile}
                      title='Remove JD file'
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </section>
        </div>
      </main>

      {/* Floating / Sticky Control Section */}
      <div className='coreops-upload-analyze-section'>
        {errorMessage && (
          <div className='coreops-error-message'>
            <FontAwesomeIcon icon={faInfoCircle} className='err-icon' />
            <span>{errorMessage}</span>
          </div>
        )}

        <div className='action-bar-card'>
          <div className='action-bar-info'>
            <span className='info-badge'>
              <strong>{selectedFiles.length}</strong> {selectedFiles.length === 1 ? 'Resume' : 'Resumes'} Ready
            </span>
            {selectedJobRole && (
              <span className='info-badge role-badge'>
                Target: <strong>{selectedJobRole}</strong>
              </span>
            )}
          </div>

          <button
            className={`coreops-button coreops-button-primary analyze-button ${
              isLoading || isAnalyzing ? 'loading' : ''
            }`}
            onClick={handleFileUpload}
            disabled={isAnalyzeButtonDisabled}
            title={
              isAnalyzeButtonDisabled
                ? !selectedFiles.length
                  ? 'Please upload candidate resumes first'
                  : 'Please provide a job description'
                : 'Analyze Resumes with AI'
            }
          >
            {isAnalyzing || isLoading ? (
              <>
                <ClipLoader color='#ffffff' size={20} />
                <span>Processing in Background...</span>
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faCogs} className='btn-icon' />
                <span>Run Intelligent Analysis</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Footer */}
      <footer className='coreops-footer'>
        <p>© {new Date().getFullYear()} COREHire.AI — Enterprise Talent Intelligence Platform</p>
      </footer>
    </div>
  );
}

export default FileUpload;

